import { Hero } from "@/.storyblok/types/319751/storyblok-components";
import { BASE_PATH, version, getClient } from "./storyblok-client";
import { getStory } from "./storyblok-api";
import { fetchLayoutBlocks } from "./storyblok-layout";
import { logger } from '@/lib/logger';
import { SbBlokData } from "@storyblok/react";

async function fetchBlogPostPreviews() {
  try {
    const storyblokApi = getClient();
    const perPage = 20;
    let page = 1;
    let hasMoreStories = true;
    let allStories: any[] = [];
    const timestamp = Date.now();

    while (hasMoreStories) {
      const response = await storyblokApi.get(
        "cdn/stories",
        {
          starts_with: `${BASE_PATH}/blog`,
          is_startpage: false,
          resolve_relations: ["blog_page_bulletproof.blog_author"],
          sort_by: "content.blog_details.date:desc",
          version,
          per_page: perPage,
          page: page,
          cv: timestamp,
        },
        {
          next: { revalidate: 3600 }
        }
      );

      const stories = response?.data?.stories || [];

      if (stories.length > 0) {
        allStories = [...allStories, ...stories];

        if (stories.length < perPage) {
          hasMoreStories = false;
        } else {
          page++;
        }
      } else {
        hasMoreStories = false;
      }
    }

    return allStories.map((story: any) => ({
      slug: story.slug,
      name: story.name,
      content: story.content,
    })) || [];

  } catch (error) {
    logger.error("Error fetching blog post previews:", error);
    return [];
  }
}

export async function getBlogHomeContent() {
  try {
    const blogHomeStory = await getStory(
      `${BASE_PATH}/blog`,
      [
        "blog_homepage_bulletproof.featured_top",
        "blog_homepage_bulletproof.accreditations_big",
        "blog_selector.select_story",
        "blog_page_bulletproof.blog_author",
      ],
      null
    );

    // logger.log(`blogHomeStory: ` + JSON.stringify(blogHomeStory, null, 2));

    const layoutBlocks = await fetchLayoutBlocks();

    const content = blogHomeStory?.content;
    const heroBlock =
      content?.hero?.find(
        (block: Hero & SbBlokData) => block.component === "hero"
      ) || null;

    const allBlogPosts = await fetchBlogPostPreviews();
    logger.log(`allBlogPosts: ` + allBlogPosts.length);
    // logger.log(`allBlogPosts: ` + JSON.stringify(allBlogPosts, null, 2));

    return {
      blogHomeStory,
      headerBlock: layoutBlocks.headerBlock,
      footerBlock: layoutBlocks.footerBlock,
      trustSmallBlock: layoutBlocks.trustSmallBlock,
      trustSmallHeaderBlock: layoutBlocks.trustSmallHeaderBlock,
      customScriptBlock: layoutBlocks.customScriptBlock,
      heroBlock,
      allBlogPosts,
    };
  } catch (error) {
    logger.error("Error fetching blog home content:", error);
    return {
      blogHomeStory: null,
      headerBlock: null,
      footerBlock: null,
      trustSmallBlock: null,
      trustSmallHeaderBlock: null,
      customScriptBlock: null,
      heroBlock: null,
      allBlogPosts: [],
    };
  }
}

export async function getBlogPostContent(slug: string) {
  try {
    const blogPostStory = await getStory(
      `${BASE_PATH}/blog/${slug}`,
      [
        "blog_page_bulletproof.accreditations_big",
        "blog_page_bulletproof.blog_author",
        "related_resources.select_story",
        "blog_page_bulletproof.related_resources",
      ],
      null
    );

    // logger.log(`blogPostStory: ` + JSON.stringify(blogPostStory, null, 2));

    const { headerBlock, footerBlock, trustSmallBlock, trustSmallHeaderBlock, customScriptBlock } =
      await fetchLayoutBlocks();

    const content = blogPostStory?.content;
    const heroBlock =
      content?.body?.find(
        (block: Hero & SbBlokData) => block.component === "hero"
      ) || null;

    return {
      blogPostStory: blogPostStory,
      headerBlock,
      footerBlock,
      trustSmallBlock,
      trustSmallHeaderBlock,
      customScriptBlock,
      heroBlock,
    };
  } catch (error) {
    logger.error(`Error fetching blog post content (${slug}):`, error);
    return {
      blogPostStory: null,
      headerBlock: null,
      footerBlock: null,
      trustSmallBlock: null,
      trustSmallHeaderBlock: null,
      customScriptBlock: null,
      heroBlock: null,
    };
  }
}

export async function getBlogAuthorContent(slug: string) {
  try {
    const authorStory = await getStory(
      `${BASE_PATH}/blog/author/${slug}`,
      [
        "blog_author.blog_author",
        "related_resources.select_story"
      ],
      null
    );

    const { headerBlock, footerBlock, trustSmallBlock, trustSmallHeaderBlock, customScriptBlock } =
      await fetchLayoutBlocks();

    const storyblokApi = getClient();
    const authorPosts = await storyblokApi.get(
      "cdn/stories",
      {
        starts_with: `${BASE_PATH}/blog`,
        is_startpage: false,
        filter_query: {
          'blog_author': {
            'in_array': authorStory.uuid
          }
        },
        version,
        per_page: 20,
        resolve_links: 'story',
      },
    );

    logger.log('Author UUID:', authorStory.uuid);
    logger.log('Total Posts Found:', authorPosts?.data?.stories?.length || 0);

    return {
      authorStory,
      headerBlock,
      footerBlock,
      trustSmallBlock,
      trustSmallHeaderBlock,
      customScriptBlock,
      authorPosts: authorPosts?.data?.stories || []
    };
  } catch (error) {
    logger.error(`Error fetching blog author content (${slug}):`, error);
    return {
      authorStory: null,
      headerBlock: null,
      footerBlock: null,
      trustSmallBlock: null,
      trustSmallHeaderBlock: null,
      customScriptBlock: null,
      authorPosts: []
    };
  }
}